import React, { useState, useEffect } from 'react';

const LiveCourse = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const resources = [
    {
      id: 1,
      type: 'Article',
      title: 'High-Income Skills to Learn in 2025',
      image: '/api/placeholder/800/400',
      lastUpdated: 'Jul 31, 2025',
      gradient: 'from-blue-900 via-blue-800 to-blue-700',
      illustration: '💼'
    },
    {
      id: 2,
      type: 'Webinar',
      title: 'AI and Machine Learning Trends for Professionals',
      image: '/api/placeholder/800/400',
      lastUpdated: 'Aug 01, 2025',
      gradient: 'from-purple-900 via-purple-800 to-purple-700',
      illustration: '🤖'
    },
    {
      id: 3,
      type: 'Article',
      title: 'Remote Work Best Practices in 2025',
      image: '/api/placeholder/800/400',
      lastUpdated: 'Aug 02, 2025',
      gradient: 'from-green-900 via-green-800 to-green-700',
      illustration: '🏠'
    },
    {
      id: 4,
      type: 'Webinar',
      title: 'Digital Marketing Strategies That Work',
      image: '/api/placeholder/800/400',
      lastUpdated: 'Aug 03, 2025',
      gradient: 'from-orange-900 via-orange-800 to-orange-700',
      illustration: '📱'
    },
    {
      id: 5,
      type: 'Article',
      title: 'Cybersecurity Essentials for Businesses',
      image: '/api/placeholder/800/400',
      lastUpdated: 'Aug 04, 2025',
      gradient: 'from-red-900 via-red-800 to-red-700',
      illustration: '🔒'
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % resources.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [resources.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % resources.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + resources.length) % resources.length);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-5xl font-bold text-gray-900 mb-0">RESOURCES</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore insights, tips, and articles written by experts in a range of professional domains.
          </p>
          <div className="w-24 h-1 bg-yellow-400 mx-auto mt-6"></div>
        </div>

        {/* Slider Container */}
        <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          <div 
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {resources.map((resource, index) => (
              <div key={resource.id} className="w-full flex-shrink-0">
                <div className="flex flex-col lg:flex-row min-h-[450px]">
                  {/* Left Side - Content */}
                  <div className={`lg:w-1/2 bg-gradient-to-br ${resource.gradient} p-12 flex flex-col justify-center relative overflow-hidden`}>
                    {/* Background Pattern */}
                    <div className="!absolute inset-0 opacity-10">
                      <div className="!absolute top-10 right-10 text-8xl transform rotate-12">
                        {resource.illustration}
                      </div>
                      <div className="!absolute bottom-10 left-10 text-6xl transform -rotate-12 opacity-50">
                        💡
                      </div>
                    </div>
                    
                    {/* Floating Money/Success Icons */}
                    <div className="!absolute top-16 right-20 animate-bounce">
                      <div className="w-8 h-8 bg-green-400 rounded transform rotate-12 opacity-80"></div>
                    </div>
                    <div className="!absolute top-32 right-32 animate-bounce delay-300">
                      <div className="w-6 h-6 bg-green-300 rounded transform -rotate-12 opacity-70"></div>
                    </div>
                    <div className="!absolute top-24 right-40 animate-bounce delay-500">
                      <div className="w-10 h-6 bg-green-500 rounded transform rotate-45 opacity-60"></div>
                    </div>
                    
                    {/* Arrow pointing up */}
                    <div className="!absolute bottom-20 right-16">
                      <div className="w-0 h-0 border-l-[20px] border-r-[20px] border-b-[40px] border-l-transparent border-r-transparent border-b-blue-400 transform rotate-12 animate-pulse"></div>
                    </div>
                    
                    {/* Staircase Steps */}
                    <div className="!absolute bottom-0 right-0 opacity-30">
                      <div className="flex items-end space-x-1">
                        <div className="w-8 h-4 bg-blue-400 rounded-t"></div>
                        <div className="w-8 h-8 bg-blue-300 rounded-t"></div>
                        <div className="w-8 h-12 bg-blue-200 rounded-t"></div>
                        <div className="w-8 h-16 bg-blue-100 rounded-t"></div>
                      </div>
                    </div>

                    {/* Main Content */}
                    <div className="relative z-10">
                      <div className="flex items-center mb-6">
                        <div className="w-12 h-12 bg-orange-400 rounded-lg flex items-center justify-center mr-4">
                          <span className="text-white font-bold text-lg">S</span>
                        </div>
                        <span className="text-orange-400 font-semibold text-lg">yslearn</span>
                      </div>
                      
                      <h2 className="text-5xl font-bold text-white mb-6 leading-tight">
                        {resource.title.split(' ').slice(0, 2).join(' ')}
                        <br />
                        <span className="text-4xl">
                          {resource.title.split(' ').slice(2).join(' ')}
                        </span>
                      </h2>
                    </div>
                  </div>

                  {/* Right Side - Info */}
                  <div className="lg:w-1/2 bg-white p-12 flex flex-col justify-center">
                    <div className="max-w-md">
                      <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-8 ${
                        resource.type === 'Article' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {resource.type}
                      </span>
                      
                      <h3 className="text-4xl font-bold text-gray-900 mb-8 leading-tight">
                        {resource.title}
                      </h3>
                      
                      <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                        {resource.type === 'Article' 
                          ? 'Discover the most in-demand skills that can significantly boost your earning potential and career growth in the current market.'
                          : 'Join industry experts as they share actionable strategies and insights that you can implement immediately in your professional journey.'
                        }
                      </p>
                      
                      <div className="flex items-center text-gray-500 text-sm">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        Last updated on {resource.lastUpdated}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="h-16 !absolute left-6 top-1/2 transform -translate-y-1/2 bg-[#264b82] bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={nextSlide}
            className="h-16 !absolute right-6 top-1/2 transform -translate-y-1/2 bg-[#264b82] bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center mt-8 space-x-3">
          {resources.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-blue-600 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveCourse;